import React, { useRef, useState } from 'react';
import { ImagePlus, Image as ImageIcon, Video, PlaySquare, X, Youtube } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';
import { EditorCard, inputCls } from './ui';

const px = 'auto=compress&cs=tinysrgb&w=1280';
export const PRESET_COVERS = [
  `https://images.pexels.com/photos/8438967/pexels-photo-8438967.jpeg?${px}`,
  'https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=1280&q=80',
  'https://images.unsplash.com/photo-1780034766295-43db0f2a0fb7?w=1280&q=80',
  'https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?w=1280&q=80',
];

const DropZone = ({ accept, onFile, Icon, title, hint, testId }) => {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <button type="button" data-testid={testId} onClick={() => ref.current?.click()}
      onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      className={`w-full rounded-2xl border-2 border-dashed py-8 px-4 flex flex-col items-center gap-2 transition-colors ${over ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}>
      <Icon className="w-8 h-8 text-blue-600" />
      <span className="text-sm font-medium text-slate-700">{title}</span>
      <span className="text-xs text-slate-400">{hint}</span>
      <input ref={ref} type="file" accept={accept} hidden onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </button>
  );
};

export const CoverCard = ({ form, setForm }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const preview = form.imagePreview || form.cover;
  return (
    <EditorCard testId="course-cover-card" Icon={ImageIcon} title={L('صورة الكورس', 'Course image')} desc={L('اختر صورة جذابة تمثل الكورس', 'Pick an attractive image for the course')}>
      {preview ? (
        <div className="relative rounded-2xl overflow-hidden aspect-video bg-slate-100 mb-3">
          <img data-testid="course-cover-preview" src={preview} alt="" className="w-full h-full object-cover" />
          <button type="button" data-testid="course-cover-remove" onClick={() => setForm(f => ({ ...f, image: null, imagePreview: '', cover: '' }))}
            className="absolute top-2 end-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-rose-500 flex items-center justify-center shadow"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="mb-3">
          <DropZone testId="course-cover-dropzone" accept="image/*" Icon={ImagePlus} title={L('اسحب الصورة هنا أو اضغط لاختيار صورة', 'Drag an image here or click to choose')} hint={L('PNG, JPG, WebP (مقاس 1280×720)', 'PNG, JPG, WebP (1280×720)')}
            onFile={f => setForm(x => ({ ...x, image: f, imagePreview: URL.createObjectURL(f) }))} />
        </div>
      )}
      <div className="grid grid-cols-4 gap-2">
        {PRESET_COVERS.map((u, i) => (
          <button key={u} type="button" data-testid={`course-cover-preset-${i}`} onClick={() => setForm(f => ({ ...f, cover: u, image: null, imagePreview: '' }))}
            className={`aspect-[4/3] rounded-xl overflow-hidden ring-2 transition-[box-shadow] ${form.cover === u && !form.image ? 'ring-blue-600 ring-offset-2' : 'ring-transparent hover:ring-blue-200'}`}>
            <img src={u} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </EditorCard>
  );
};

export const IntroVideoCard = ({ form, setForm, set }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <EditorCard testId="course-video-card" Icon={Video} title={L('فيديو المقدمة (اختياري)', 'Intro video (optional)')} desc={L('يمكنك رفع فيديو قصير للتعريف بالكورس', 'Upload a short video introducing the course')}>
      {form.videoFileName || form.video_url ? (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 mb-3">
          <PlaySquare className="w-5 h-5 text-blue-600 shrink-0" />
          <span data-testid="course-video-name" className="flex-1 min-w-0 truncate text-sm text-slate-700" dir="ltr">{form.videoFileName || form.video_url.split('/').pop()}</span>
          {form.videoFileName
            ? <button type="button" onClick={() => setForm(f => ({ ...f, videoFile: null, videoFileName: '' }))} className="text-rose-500"><X className="w-4 h-4" /></button>
            : <button type="button" data-testid="course-video-remove" onClick={() => setForm(f => ({ ...f, video_url: '' }))} className="text-rose-500"><X className="w-4 h-4" /></button>}
        </div>
      ) : null}
      <DropZone testId="course-video-dropzone" accept="video/*" Icon={PlaySquare} title={L('اضغط لرفع فيديو المقدمة', 'Click to upload intro video')} hint={L('MP4, WebM, MOV', 'MP4, WebM, MOV')}
        onFile={f => setForm(x => ({ ...x, videoFile: f, videoFileName: f.name }))} />
      <div className="relative mt-3">
        <Youtube className="absolute top-1/2 -translate-y-1/2 start-3.5 w-4 h-4 text-rose-500 pointer-events-none" />
        <input data-testid="course-youtube" dir="ltr" className={`${inputCls} ps-10`} placeholder={L('أو رابط يوتيوب', 'Or a YouTube link')} value={form.youtube} onChange={e => set('youtube', e.target.value)} />
      </div>
    </EditorCard>
  );
};
